import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MAX_LIST_ITEMS = 20;
const MAX_TEXT_LENGTH = 1200;

const aiAdvisorAxios = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

const toArray = (value) => (Array.isArray(value) ? value : []);

const truncateText = (value, maxLength = MAX_TEXT_LENGTH) => {
  if (typeof value !== 'string') return value;
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}...`;
};

const compactList = (value, mapper) => {
  return toArray(value).slice(0, MAX_LIST_ITEMS).map(mapper);
};

const compactCourseData = (courseData) => {
  return compactList(courseData, (course) => ({
    id: course?.id,
    course: course?.course,
    disableUnits: !!course?.disableUnits,
    overallGrade: course?.overallGrade,
    predictedGrade: course?.predictedGrade,
    subjects: compactList(course?.subjects, (subject) => ({
      title: truncateText(subject?.title, 120),
      predictedGrade: subject?.predictedGrade,
      actualGrade: subject?.actualGrade,
    })),
    modules: compactList(course?.modules, (module) => ({
      id: module?.id,
      name: module?.name,
      achievedGrade: module?.achievedGrade,
      predictedGrade: module?.predictedGrade,
      units: compactList(module?.units, (unit) => ({
        id: unit?.id,
        name: unit?.name,
        achievedGrade: unit?.achievedGrade,
        predictedGrade: unit?.predictedGrade,
      })),
    })),
  }));
};

const buildCompactUserData = (userData) => {
  if (!userData || typeof userData !== 'object') return userData;

  return {
    ...userData,
    profileFields: compactList(userData.profileFields, (field) => ({
      title: field?.title || field?.defaultTitle,
      value: truncateText(field?.value),
    })),
    dynamicProfileFields: compactList(userData.dynamicProfileFields, (field) => ({
      title: field?.title || field?.defaultTitle,
      value: truncateText(field?.value),
    })),
    customFields: compactList(userData.customFields, (field) => ({
      title: field?.title || field?.defaultTitle,
      value: truncateText(field?.value),
    })),
    workExperience: compactList(userData.workExperience, (entry) => ({
      company: entry?.company,
      role: entry?.role,
      title: entry?.title,
      description: truncateText(entry?.description),
      startDate: entry?.startDate,
      endDate: entry?.endDate,
    })),
    transferableSkills: compactList(userData.transferableSkills, (skill) => ({
      id: skill?.id,
      skill: skill?.skill || skill?.name || skill?.title,
    })),
    topLevelVideos: compactList(userData.topLevelVideos, (video) => ({
      id: video?.id,
      title: truncateText(video?.title, 180),
      description: truncateText(video?.description),
    })),
    courseData: compactCourseData(userData.courseData),
    compulsory_gcses: compactList(userData.compulsory_gcses, (entry) => ({
      subject: entry?.subject || entry?.title,
      grade: entry?.grade || entry?.result,
    })),
    optional_gcses: compactList(userData.optional_gcses, (entry) => ({
      subject: entry?.subject || entry?.title,
      grade: entry?.grade || entry?.result,
    })),
    academic_qualifications: compactList(userData.academic_qualifications, (entry) => ({
      qualification: entry?.qualification || entry?.title,
      subject: entry?.subject,
      grade: entry?.grade || entry?.result,
    })),
  };
};

/**
 * Analyze the user's entire profile and get AI suggestions
 * Returns: { advice, completeness, filled[], missing[] }
 */
export const analyzeProfile = async (userData) => {
  const token = localStorage.getItem('token');
  const requestHeaders = {
    'Authorization': `${token}`,
  };

  const fullPayload = userData && typeof userData === 'object' ? userData : {};
  const compactPayload = buildCompactUserData(fullPayload);
  const fallbackRequests = [
    { name: 'wrapped-full', payload: { userData: fullPayload } },
    { name: 'direct-full', payload: fullPayload },
    { name: 'wrapped-compact', payload: { userData: compactPayload } },
  ];

  let lastError = null;

  try {
    console.log('[AI Service] Calling:', '/ai/analyze-profile');
    for (let index = 0; index < fallbackRequests.length; index += 1) {
      const attempt = fallbackRequests[index];
      try {
        const response = await aiAdvisorAxios.post('/ai/analyze-profile', attempt.payload, {
          headers: requestHeaders,
        });
        if (index > 0) {
          console.info(`[AI Service] Recovered with retry mode: ${attempt.name}`);
        }
        return response.data;
      } catch (error) {
        lastError = error;
        const status = error?.response?.status;
        const canRetry = (status === 500 || status === 413 || status === 422) && index < fallbackRequests.length - 1;
        if (!canRetry) {
          throw error;
        }
        console.warn(`[AI Service] Attempt "${attempt.name}" failed (${status}), retrying with safer payload...`);
      }
    }
  } catch (error) {
    console.error('AI Profile Analysis failed:', error?.response?.data || error);
    throw lastError || error;
  }
};

/**
 * Analyze a specific section of the profile
 * @param {string} section - Section name (e.g., "workExperience", "bio")
 * @param {object} data - Section data to analyze
 * Returns: { advice, section }
 */
export const analyzeSection = async (section, data, clubInfo) => {
  const token = localStorage.getItem('token');
  try {
    const response = await aiAdvisorAxios.post('/ai/analyze-section', {
      section,
      data,
      clubInfo,
    }, {
      headers: {
        'Authorization': `${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('AI Section Analysis failed:', error);
    throw error;
  }
};
